import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type {
  ActionDetailDto,
  ActionListBodyDto,
  ActionListQuery,
  ApprovalCountDto,
  ApprovalInboxDetailDto,
  ApprovalListBodyDto,
  ApprovalListQuery,
  ApproveResultDto,
  DecisionResultDto,
  ProposeBodyDto,
  ProposeResultDto,
} from '../models/platformAction.wire'

import { appHttp, unwrap, unwrapEnvelope } from '@/shared/http'

/**
 * The platform-actions slice's only HTTP boundary. Endpoints and bodies come from the backend
 * handoff package (`docs/handoffs/platform_actions/openapi.yaml` in orphex_django_rag).
 *
 * Every call is feature-owned (`suppressGlobalErrorHandler`): the README's error-code table needs
 * per-code UX — a guardrail violation belongs in the modal, an expired token triggers a
 * re-propose — so a global toast would be wrong for all but the transport failures.
 */

function requestConfig(
  signal?: AbortSignal,
  params?: ActionListQuery | ApprovalListQuery,
): AxiosRequestConfig {
  const config: AxiosRequestConfig = {
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true },
  }
  if (signal) config.signal = signal
  if (params) config.params = params
  return config
}

export class PlatformActionApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  private base(workspaceId: number): string {
    return `/workspaces/${workspaceId}/platform-actions/`
  }

  /**
   * List requests for the workspace.
   *
   * The ONLY endpoint here that is not the app's `{status, data}` envelope: `pagination` is a
   * SIBLING of `data`, so `unwrapEnvelope` would silently return the rows and drop the page
   * counts. Read the flat body and hand both halves to the service.
   */
  async list(
    workspaceId: number,
    query: ActionListQuery = {},
    signal?: AbortSignal,
  ): Promise<ActionListBodyDto> {
    const response = await this.http.get(this.base(workspaceId), requestConfig(signal, query))
    return unwrap<ActionListBodyDto>(response)
  }

  /** Propose an action — 201 carries the `pending_approval` request plus a 15-minute token. */
  async propose(
    workspaceId: number,
    body: ProposeBodyDto,
    signal?: AbortSignal,
  ): Promise<ProposeResultDto> {
    const response = await this.http.post(this.base(workspaceId), body, requestConfig(signal))
    return unwrapEnvelope<ProposeResultDto>(response)
  }

  /**
   * Propose the inverse of a succeeded request. The response is the **same shape as propose**
   * (`request` + `confirm_token`) because an undo IS a new pending request — nothing is reverted
   * until it is confirmed through the ordinary approval path.
   *
   * No body: the backend derives the inverse from the original's preview.
   */
  async revert(
    workspaceId: number,
    actionId: string,
    signal?: AbortSignal,
  ): Promise<ProposeResultDto> {
    const response = await this.http.post(
      `${this.base(workspaceId)}${actionId}/revert/`,
      undefined,
      requestConfig(signal),
    )
    return unwrapEnvelope<ProposeResultDto>(response)
  }

  /** Request detail with its approval and audit chains — also the polling endpoint. */
  async detail(
    workspaceId: number,
    actionId: string,
    signal?: AbortSignal,
  ): Promise<ActionDetailDto> {
    const response = await this.http.get(
      `${this.base(workspaceId)}${actionId}/`,
      requestConfig(signal),
    )
    return unwrapEnvelope<ActionDetailDto>(response)
  }

  /** Approve the previewed request and enqueue execution. A stale token rejects with 409. */
  async confirm(
    workspaceId: number,
    actionId: string,
    confirmToken: string,
    comment?: string,
    signal?: AbortSignal,
  ): Promise<DecisionResultDto> {
    const body: { confirm_token: string; comment?: string } = { confirm_token: confirmToken }
    if (comment) body.comment = comment
    const response = await this.http.post(
      `${this.base(workspaceId)}${actionId}/confirm/`,
      body,
      requestConfig(signal),
    )
    return unwrapEnvelope<DecisionResultDto>(response)
  }

  async reject(
    workspaceId: number,
    actionId: string,
    comment?: string,
    signal?: AbortSignal,
  ): Promise<DecisionResultDto> {
    const body: { comment?: string } = {}
    if (comment) body.comment = comment
    const response = await this.http.post(
      `${this.base(workspaceId)}${actionId}/reject/`,
      body,
      requestConfig(signal),
    )
    return unwrapEnvelope<DecisionResultDto>(response)
  }

  /**
   * Record an approver's decision. Takes no confirm token and moves the request nowhere: the
   * proposer still runs it. The backend checks the route role, the policy's `required_role` and
   * maker-checker independently and answers 403 with a sentence the UI prints verbatim.
   */
  async approve(
    workspaceId: number,
    actionId: string,
    comment?: string,
    signal?: AbortSignal,
  ): Promise<ApproveResultDto> {
    const body: { comment?: string } = {}
    if (comment) body.comment = comment
    const response = await this.http.post(
      `${this.base(workspaceId)}${actionId}/approve/`,
      body,
      requestConfig(signal),
    )
    return unwrapEnvelope<ApproveResultDto>(response)
  }

  /**
   * The approval inbox — the gated set. Flat body like `list`: `pagination` (with
   * `scan_truncated`) is a SIBLING of `data`, so it is read with `unwrap`, not `unwrapEnvelope`.
   */
  async listApprovals(
    workspaceId: number,
    query: ApprovalListQuery = {},
    signal?: AbortSignal,
  ): Promise<ApprovalListBodyDto> {
    const response = await this.http.get(
      `${this.base(workspaceId)}approvals/`,
      requestConfig(signal, query),
    )
    return unwrap<ApprovalListBodyDto>(response)
  }

  /** The tab badge: how many requests wait on a decision. */
  async approvalsCount(workspaceId: number, signal?: AbortSignal): Promise<ApprovalCountDto> {
    const response = await this.http.get(
      `${this.base(workspaceId)}approvals/count/`,
      requestConfig(signal),
    )
    return unwrapEnvelope<ApprovalCountDto>(response)
  }

  /** One inbox row with its decision trail — what an approver decides from. */
  async approvalDetail(
    workspaceId: number,
    actionId: string,
    signal?: AbortSignal,
  ): Promise<ApprovalInboxDetailDto> {
    const response = await this.http.get(
      `${this.base(workspaceId)}approvals/${actionId}/`,
      requestConfig(signal),
    )
    return unwrapEnvelope<ApprovalInboxDetailDto>(response)
  }

  /** Cancel a request that has not started executing; 409 once execution began. */
  async cancel(
    workspaceId: number,
    actionId: string,
    signal?: AbortSignal,
  ): Promise<DecisionResultDto> {
    const response = await this.http.post(
      `${this.base(workspaceId)}${actionId}/cancel/`,
      {},
      requestConfig(signal),
    )
    return unwrapEnvelope<DecisionResultDto>(response)
  }
}

export const platformActionApi = new PlatformActionApi()
